import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, CheckCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import V2AuthInput from "./components/V2AuthInput";
import V2AuthButton from "./components/V2AuthButton";
import V2PasswordValidation, {
  validatePassword,
} from "./components/V2PasswordValidation";

export default function V2ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("form"); // form, success, invalid-token

  const { allValid } = validatePassword(password, confirmPassword);

  // Check for token on mount
  useEffect(() => {
    if (!token) {
      setStatus("invalid-token");
      setError(i18n.t("auth.resetPassword.noToken"));
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!allValid) {
      setError(t("auth.common.passwordRequirementsError"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await betaAPI.confirmPasswordReset({ token, password });
      setStatus("success");
      showToast.success(t("auth.resetPassword.resetSuccess"));
    } catch (err) {
      console.error("Password reset error:", err);

      if (err.status === 400 || err.status === 404) {
        setStatus("invalid-token");
        setError(t("auth.resetPassword.invalidLink"));
      } else {
        setError(err.message || t("auth.resetPassword.failed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Invalid token state
  if (status === "invalid-token") {
    return (
      <>
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <XCircle size={28} className="text-red-600" />
          </div>
        </div>

        <div className="mb-6 text-center">
          <h1 className="mb-2 text-xl font-bold text-[#111827]">
            {t("auth.resetPassword.invalidTitle")}
          </h1>
          <p className="text-sm text-[#6B7280]">{error}</p>
        </div>

        <V2AuthButton
          variant="primary"
          onClick={() => navigate("/v2/auth/forgot-password")}
        >
          {t("auth.resetPassword.requestNewLink")}
        </V2AuthButton>

        <div className="mt-4 text-center">
          <Link
            to="/v2/auth/login"
            className="text-sm text-[#6B7280] transition-colors hover:text-[#374151]"
          >
            {t("auth.common.backToLogin")}
          </Link>
        </div>
      </>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <>
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle size={28} className="text-green-600" />
          </div>
        </div>

        <div className="mb-6 text-center">
          <h1 className="mb-2 text-xl font-bold text-[#111827]">
            {t("auth.resetPassword.successTitle")}
          </h1>
          <p className="text-sm text-[#6B7280]">
            {t("auth.resetPassword.successDescription")}
          </p>
        </div>

        <V2AuthButton
          variant="primary"
          onClick={() => navigate("/v2/auth/login")}
        >
          {t("auth.resetPassword.signIn")}
        </V2AuthButton>
      </>
    );
  }

  // Form state
  return (
    <>
      {/* Icon */}
      <div className="mb-6 flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FEF3E7]">
          <Lock size={28} className="text-[#E07A3A]" />
        </div>
      </div>

      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.resetPassword.title")}
        </h1>
        <p className="text-sm text-[#6B7280]">
          {t("auth.resetPassword.subtitle")}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* New Password */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            {t("auth.resetPassword.newPassword")}
          </label>
          <V2AuthInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.resetPassword.newPasswordPlaceholder")}
            error={!!error}
            disabled={isLoading}
            autoComplete="new-password"
          />
        </div>

        {/* Confirm Password */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            {t("auth.resetPassword.confirmNewPassword")}
          </label>
          <V2AuthInput
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t("auth.resetPassword.confirmNewPasswordPlaceholder")}
            error={!!error}
            disabled={isLoading}
            autoComplete="new-password"
          />
        </div>

        {/* Password validation */}
        <V2PasswordValidation
          password={password}
          confirmPassword={confirmPassword}
        />

        {/* Error message */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Submit button */}
        <V2AuthButton
          type="submit"
          variant="primary"
          disabled={!allValid}
          loading={isLoading}
        >
          {t("auth.resetPassword.submit")}
        </V2AuthButton>
      </form>

      {/* Back to login */}
      <div className="mt-6 text-center">
        <Link
          to="/v2/auth/login"
          className="text-sm text-[#6B7280] transition-colors hover:text-[#374151]"
        >
          {t("auth.common.backToLogin")}
        </Link>
      </div>
    </>
  );
}
